import React, { useMemo, useState } from "react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ReferenceLine } from "recharts";
import { TrendingUp, TrendingDown, Wallet, Landmark, Info } from "lucide-react";
import { formatBRL } from "../context/AppContext";

const MES3 = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const INTERVALO = { mensal: 1, trimestral: 3, semestral: 6, anual: 12 };
const absM = (y, m) => y * 12 + m;
const ymFromAbs = (a) => [Math.floor((a - 1) / 12), ((a - 1) % 12) + 1];
const ym = (y, m) => `${y}-${String(m).padStart(2, "0")}`;
const rotulo = (k) => { const [y, m] = k.split("-"); return `${MES3[+m - 1]}/${y.slice(2)}`; };

const cicloInfo = (startIso, per, y, m) => {
  const n = INTERVALO[per || "mensal"] || 1;
  if (n <= 1) return { eh: true, meses: [[y, m]], n: 1 };
  const [sy, sm] = (startIso || "").split("-").map(Number);
  if (!sy) return { eh: true, meses: [[y, m]], n: 1 };
  const idx = absM(y, m) - absM(sy, sm);
  if (idx < 0 || idx % n !== n - 1) return { eh: false, meses: [], n };
  const base = absM(y, m); const meses = [];
  for (let k = n - 1; k >= 0; k--) meses.push(ymFromAbs(base - k));
  return { eh: true, meses, n };
};
const mesDecimo = (st, per, y) => { for (let mm = 12; mm >= 1; mm--) if (cicloInfo(st, per, y, mm).eh) return mm; return null; };
const vigente = (dataInicio, valorBase, ano, mes) => {
  const base = Number(valorBase) || 0;
  if (!base) return 0;
  const [sy, sm] = (dataInicio || "").split("-").map(Number);
  if (!sy) return base;
  const h = new Date();
  const alvo = Math.floor((absM(ano, mes) - absM(sy, sm)) / 12);
  const hoje = Math.max(0, Math.floor((absM(h.getFullYear(), h.getMonth() + 1) - absM(sy, sm)) / 12));
  return +(base * Math.pow(1.08, alvo - hoje)).toFixed(2);
};

export const projetarCaixa = ({ residentes, residenteServicos, faturas, contasPagar, contasReceber, meses = 12 }) => {
  const h = new Date();
  const ini = absM(h.getFullYear(), h.getMonth() + 1);
  const chaves = Array.from({ length: meses }, (_, i) => { const [y, m] = ymFromAbs(ini + i); return ym(y, m); });
  const linhas = chaves.map((k) => ({ mes: k, label: rotulo(k), mensalidades: 0, servicos: 0, decimo: 0, faturasReais: 0, outrasReceitas: 0, despesasRecorrentes: 0, despesasAvulsas: 0, residentes: 0 }));
  const idx = Object.fromEntries(chaves.map((k, i) => [k, i]));
  const faturasPorMes = {};
  (faturas || []).forEach((f) => { if (f.cancelada) return; const key = `${f.residenteId}_${f.mes}`; faturasPorMes[key] = (faturasPorMes[key] || 0) + (Number(f.total) || 0); });

  (residentes || []).forEach((r) => {
    if ((r.status || "Ativo") !== "Ativo") return;
    const base = Number(r.valorMensalidade) || 0;
    const st = r.dataInicioContrato || r.dataInternacao || "";
    const per = r.periodicidadePagamento || "mensal";
    const [iy, im] = (r.dataInternacao || r.dataInicioContrato || "").split("-").map(Number);
    chaves.forEach((k) => {
      const [y, m] = k.split("-").map(Number);
      const L = linhas[idx[k]];
      const real = faturasPorMes[`${r.id}_${k}`];
      if (real) { L.faturasReais += real; L.residentes += 1; return; }
      const info = cicloInfo(st, per, y, m);
      if (!info.eh) return;
      L.residentes += 1;
      if (base > 0) {
        let soma = 0;
        info.meses.forEach(([yy, mm]) => { if (!iy || absM(yy, mm) >= absM(iy, im || 1)) soma += vigente(st, base, yy, mm); });
        L.mensalidades += soma;
        if (r.cobrarDecimoTerceiro && mesDecimo(st, per, y) === m) {
          const nMeses = info.n > 1 ? 12 : (iy === y ? Math.max(0, 13 - (im || 1)) : 12);
          if (nMeses > 0) L.decimo += +((vigente(st, base, y, 12) / 12) * nMeses).toFixed(2);
        }
      }
      (residenteServicos || []).filter((s) => s.residenteId === r.id && s.ativo !== false && s.recorrente).forEach((s) => {
        const v = Number(s.valor) || 0;
        if (v <= 0) return;
        let soma = 0;
        info.meses.forEach(([yy, mm]) => { soma += vigente(st, v, yy, mm); });
        L.servicos += soma;
      });
    });
  });

  (contasReceber || []).forEach((c) => {
    const k = (c.vencimento || "").slice(0, 7);
    if (idx[k] === undefined) return;
    linhas[idx[k]].outrasReceitas += Number(c.valor) || 0;
  });
  (contasPagar || []).forEach((c) => {
    const k = (c.vencimento || "").slice(0, 7);
    if (idx[k] === undefined) return;
    const L = linhas[idx[k]];
    if (c.recorrente) L.despesasRecorrentes += Number(c.valor) || 0; else L.despesasAvulsas += Number(c.valor) || 0;
  });

  let acumulado = 0;
  return linhas.map((L) => {
    const receitas = +(L.mensalidades + L.servicos + L.decimo + L.faturasReais + L.outrasReceitas).toFixed(2);
    const despesas = +(L.despesasRecorrentes + L.despesasAvulsas).toFixed(2);
    const saldo = +(receitas - despesas).toFixed(2);
    acumulado = +(acumulado + saldo).toFixed(2);
    return { ...L, receitas, despesas, saldo, acumulado };
  });
};

const TooltipCaixa = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const li = (t, v, cls = "text-slate-600") => <div className={`flex justify-between gap-6 ${cls}`}><span>{t}</span><b>{formatBRL(v)}</b></div>;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-xs space-y-1 min-w-[260px]">
      <p className="font-semibold text-slate-800 mb-1">{label} · {d.residentes} residente(s) faturando</p>
      {d.faturasReais > 0 && li("Faturas já geradas", d.faturasReais, "text-blue-700")}
      {d.mensalidades > 0 && li("Mensalidades (projeção)", d.mensalidades, "text-blue-700")}
      {d.servicos > 0 && li("Serviços recorrentes", d.servicos, "text-blue-700")}
      {d.decimo > 0 && li("13ª mensalidade", d.decimo, "text-blue-700")}
      {d.outrasReceitas > 0 && li("Outros recebíveis", d.outrasReceitas, "text-blue-700")}
      {li("Despesas recorrentes", d.despesasRecorrentes, "text-rose-700")}
      {d.despesasAvulsas > 0 && li("Despesas avulsas/parceladas", d.despesasAvulsas, "text-rose-700")}
      <div className="border-t border-slate-100 pt-1 mt-1">{li("Saldo do mês", d.saldo, d.saldo >= 0 ? "text-emerald-700" : "text-red-700")}</div>
      {li("Saldo acumulado", d.acumulado, d.acumulado >= 0 ? "text-emerald-700" : "text-red-700")}
    </div>
  );
};

export const PrevisaoCaixa = ({ residentes, residenteServicos, faturas, contasPagar, contasReceber }) => {
  const [horizonte, setHorizonte] = useState(6);
  const dados = useMemo(() => projetarCaixa({ residentes, residenteServicos, faturas, contasPagar, contasReceber, meses: horizonte }), [residentes, residenteServicos, faturas, contasPagar, contasReceber, horizonte]);
  const totR = dados.reduce((s, d) => s + d.receitas, 0);
  const totD = dados.reduce((s, d) => s + d.despesas, 0);
  const saldo = +(totR - totD).toFixed(2);
  const negativos = dados.filter((d) => d.saldo < 0);
  const fmtK = (v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v));

  return (
    <div className="bg-white rounded-xl border border-slate-100 shadow-sm" data-testid="previsao-caixa">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-slate-100">
        <div>
          <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Landmark className="w-4 h-4 text-emerald-600" /> Previsão de Caixa</h3>
          <p className="text-xs text-slate-500 mt-0.5">Receitas projetadas (mensalidades com reajuste, serviços recorrentes, 13ª e faturas já geradas) versus despesas lançadas (recorrentes e avulsas), por mês de vencimento.</p>
        </div>
        <div className="flex items-center gap-1 text-xs" data-testid="previsao-horizonte">
          {[3, 6, 12].map((n) => (
            <button key={n} type="button" onClick={() => setHorizonte(n)} className={`px-2.5 py-1 rounded-full border ${horizonte === n ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`} data-testid={`previsao-horizonte-${n}`}>{n} meses</button>
          ))}
        </div>
      </div>
      <div className="p-5 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rounded-lg bg-blue-50/60 border border-blue-100 px-4 py-3" data-testid="previsao-kpi-receitas">
            <p className="text-[11px] uppercase tracking-wide text-blue-700 font-semibold flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> Receitas previstas</p>
            <p className="text-lg font-bold text-blue-800">{formatBRL(totR)}</p>
          </div>
          <div className="rounded-lg bg-rose-50/60 border border-rose-100 px-4 py-3" data-testid="previsao-kpi-despesas">
            <p className="text-[11px] uppercase tracking-wide text-rose-700 font-semibold flex items-center gap-1"><TrendingDown className="w-3.5 h-3.5" /> Despesas previstas</p>
            <p className="text-lg font-bold text-rose-800">{formatBRL(totD)}</p>
          </div>
          <div className={`rounded-lg border px-4 py-3 ${saldo >= 0 ? "bg-emerald-50/60 border-emerald-100" : "bg-red-50 border-red-200"}`} data-testid="previsao-kpi-saldo">
            <p className={`text-[11px] uppercase tracking-wide font-semibold flex items-center gap-1 ${saldo >= 0 ? "text-emerald-700" : "text-red-700"}`}><Wallet className="w-3.5 h-3.5" /> Saldo projetado ({horizonte} meses)</p>
            <p className={`text-lg font-bold ${saldo >= 0 ? "text-emerald-800" : "text-red-800"}`}>{formatBRL(saldo)}</p>
          </div>
        </div>
        {negativos.length > 0 && (
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="previsao-alerta">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Atenção: saldo negativo previsto em {negativos.map((d) => d.label).join(", ")}.
          </div>
        )}
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={dados} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtK} width={48} />
              <Tooltip content={<TooltipCaixa />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <ReferenceLine y={0} stroke="#94a3b8" />
              <Bar dataKey="receitas" name="Receitas" fill="#2563eb" radius={[4, 4, 0, 0]} />
              <Bar dataKey="despesas" name="Despesas" fill="#f43f5e" radius={[4, 4, 0, 0]} />
              <Line type="monotone" dataKey="saldo" name="Saldo do mês" stroke="#059669" strokeWidth={2.5} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="acumulado" name="Saldo acumulado" stroke="#0f172a" strokeWidth={1.5} strokeDasharray="5 4" dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="previsao-tabela">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-100">
                <th className="py-2 pr-3 font-medium">Mês</th>
                <th className="py-2 pr-3 font-medium text-right">Receitas</th>
                <th className="py-2 pr-3 font-medium text-right">Desp. recorrentes</th>
                <th className="py-2 pr-3 font-medium text-right">Desp. avulsas</th>
                <th className="py-2 pr-3 font-medium text-right">Saldo</th>
                <th className="py-2 font-medium text-right">Acumulado</th>
              </tr>
            </thead>
            <tbody>
              {dados.map((d) => (
                <tr key={d.mes} className="border-b border-slate-50" data-testid={`previsao-linha-${d.mes}`}>
                  <td className="py-2 pr-3 font-medium text-slate-700">{d.label}</td>
                  <td className="py-2 pr-3 text-right text-blue-700">{formatBRL(d.receitas)}</td>
                  <td className="py-2 pr-3 text-right text-rose-700">{formatBRL(d.despesasRecorrentes)}</td>
                  <td className="py-2 pr-3 text-right text-rose-700">{formatBRL(d.despesasAvulsas)}</td>
                  <td className={`py-2 pr-3 text-right font-semibold ${d.saldo >= 0 ? "text-emerald-700" : "text-red-700"}`}>{formatBRL(d.saldo)}</td>
                  <td className={`py-2 text-right ${d.acumulado >= 0 ? "text-slate-700" : "text-red-700"}`}>{formatBRL(d.acumulado)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PrevisaoCaixa;
